import { Component, OnDestroy, OnInit } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { NavigationEnd, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Subject, of } from 'rxjs';
import { filter, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { EcommerceOnboardingService } from './shared/services/ecommerce-onboarding.service';
import { ShopState } from './shared/ngrxState/shop.reducer';
import { StoresService } from './shared/services/stores.service';
import { AuthService } from './features/services/auth/auth.service';
import { SupportAssistantWidgetService } from './shared/services/support-assistant-widget.service';
import { CloseDropdownsOnScrollService } from './shared/services/close-dropdowns-on-scroll.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'ConversellFront';

  private readonly destroy$ = new Subject<void>();

  constructor(
    private afAuth: AngularFireAuth,
    private afs: AngularFirestore,
    private store: Store<{ shop: ShopState }>,
    private storesService: StoresService,
    private onboarding: EcommerceOnboardingService,
    private router: Router,
    private auth: AuthService,
    private supportAssistant: SupportAssistantWidgetService,
    private closeDropdownsOnScroll: CloseDropdownsOnScrollService,
  ) {}

  ngOnInit(): void {
    this.checkFirebaseInitialization();
    this.initCatalogStatusWatch();
    this.initTourRouteGuard();
    this.supportAssistant.init();
    this.closeDropdownsOnScroll.init();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.onboarding.stopCatalogWatch();
    this.closeDropdownsOnScroll.ngOnDestroy();
  }

  checkFirebaseInitialization(): void {
    // Firebase initialization check
  }

  /** Stop onboarding tour when user leaves the page scope (steps 1–4 vs step 5). */
  private initTourRouteGuard(): void {
    this.onboarding.enforceTourRouteScope(this.router.url);

    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntil(this.destroy$),
      )
      .subscribe((event) => {
        this.onboarding.enforceTourRouteScope(event.urlAfterRedirects);
      });
  }

  private initCatalogStatusWatch(): void {
    this.store
      .select((state) => state.shop.selectedShop)
      .pipe(
        takeUntil(this.destroy$),
        switchMap((shop) => {
          if (!shop?.id || !shop?.platform || !this.auth.isAdmin()) {
            return of(null).pipe(
              tap(() => this.onboarding.bindCatalogWatch(null)),
            );
          }

          this.onboarding.setWorkspaceContext(shop.id);
          this.onboarding.configureTour(shop.shopActivity, shop.platform);

          return this.storesService.getStore(shop.platform, shop.id).pipe(
            map((storeDoc) => ({ shop, storeDoc })),
          );
        }),
      )
      .subscribe((payload) => {
        if (!payload) {
          return;
        }

        const { shop, storeDoc } = payload;
        this.onboarding.configureTour(
          shop.shopActivity ?? storeDoc?.shopActivity,
          shop.platform,
        );

        const storeInstalled = this.onboarding.resolveStoreInstalled(
          shop.platform,
          storeDoc?.status,
        );

        this.onboarding.bindCatalogWatch({
          platform: shop.platform,
          storeId: shop.id,
          shopActivity: shop.shopActivity ?? storeDoc?.shopActivity,
          storeInstalled,
        });
      });
  }
}
